import {
  AuthAgentResultCode,
  AuthDebugStep,
  AuthEventType,
  AuthStepStatus,
  AuthTerminalCause,
  AuthValidationResult,
  AuthenticationDebug,
  DebugStep,
} from '@/shared/types/tryIt';

import { ConversationTurn, TurnAuthentication } from '../types/conversation';

const RESULT_OF_CAUSE: Partial<Record<AuthTerminalCause, AuthAgentResultCode>> = {
  [AuthTerminalCause.THRESHOLD_MET]: AuthAgentResultCode.SUCCESS,
  [AuthTerminalCause.MAX_ATTEMPTS]: AuthAgentResultCode.FAILURE,
  [AuthTerminalCause.TIMEOUT]: AuthAgentResultCode.FAILURE,
  [AuthTerminalCause.SYSTEM_FAILURE]: AuthAgentResultCode.SYSTEM_FAILURE,
  [AuthTerminalCause.LLM_EXIT]: AuthAgentResultCode.FAILURE,
};

const authStepsOf = (turn: ConversationTurn): AuthDebugStep[] => {
  const flatten = (steps: DebugStep[] = []): AuthDebugStep[] =>
    steps.flatMap((step) => [...(step.auth ? [step.auth] : []), ...flatten(step.sub_steps)]);
  return flatten(turn.metadata.steps);
};

// `THRESHOLD_MET` also fires when identification ends and verification begins.
const isStepExit = (exit: AuthDebugStep, following: AuthDebugStep[]) =>
  exit.terminal_cause === AuthTerminalCause.THRESHOLD_MET &&
  following.some(
    (step) => step.event === AuthEventType.AUTH_STEP_ENTERED && step.step !== exit.step
  );

const terminalExitOf = (steps: AuthDebugStep[]): AuthDebugStep | undefined =>
  steps.find(
    (step, index) =>
      step.event === AuthEventType.AUTH_EXIT && !isStepExit(step, steps.slice(index + 1))
  );

const foldTurn = (steps: AuthDebugStep[], carried?: AuthenticationDebug): AuthenticationDebug => {
  const validations = steps.filter((step) => step.event === AuthEventType.AUTH_VALIDATE_INPUT);
  const validated = validations
    .filter((step) => step.validation_result === AuthValidationResult.VALID)
    .map((step) => step.question_key)
    .filter((key) => key !== undefined);
  const failed = validations.filter(
    (step) => step.validation_result === AuthValidationResult.INVALID
  ).length;
  const errored =
    validations.filter((step) => step.validation_result === AuthValidationResult.ERROR).length +
    steps.filter((step) => step.event === AuthEventType.AUTH_VALIDATOR_SETUP_ERROR).length;

  return {
    current_step: steps.findLast((step) => step.step !== undefined)?.step ?? carried?.current_step,
    failed_attempts: (carried?.failed_attempts ?? 0) + failed,
    system_errors: (carried?.system_errors ?? 0) + errored,
    validated_question_keys: [
      ...new Set([...(carried?.validated_question_keys ?? []), ...validated]),
    ],
  };
};

export const hasAuthExit = (turn: ConversationTurn) =>
  terminalExitOf(authStepsOf(turn)) !== undefined;

export const terminalAuthOf = (
  turn: ConversationTurn,
  carried?: AuthenticationDebug
): TurnAuthentication | undefined => {
  const steps = authStepsOf(turn);
  const exit = terminalExitOf(steps);
  if (!exit) {
    return undefined;
  }
  const cause = exit.terminal_cause ?? AuthTerminalCause.UNSPECIFIED_TERMINAL_CAUSE;

  return {
    ...foldTurn(steps, carried),
    status: AuthStepStatus.COMPLETED,
    final_result_code: RESULT_OF_CAUSE[cause] ?? AuthAgentResultCode.UNSPECIFIED,
    scoreUnknown: true,
  };
};

export const fillTerminalAuth = (messages: ConversationTurn[]) => {
  let carried: AuthenticationDebug | undefined;
  for (const turn of messages) {
    const steps = authStepsOf(turn);
    const exited = terminalExitOf(steps) !== undefined;
    if (exited && !turn.metadata.authentication) {
      turn.metadata.authentication = terminalAuthOf(turn, carried);
      turn.metadata.isAuthentication = true;
    }
    if (exited) {
      carried = undefined;
      continue;
    }
    // The reported payload is cumulative; without one, fold this turn's steps in.
    if (turn.metadata.authentication) {
      carried = turn.metadata.authentication;
    } else if (steps.length > 0) {
      carried = foldTurn(steps, carried);
    }
  }
};
